import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import { merge } from 'lodash-es'
import type { DeepPartial } from 'ts-essentials'

import type {
  ActivityGroup,
  RoomActivityGroup,
  ActivityId,
  ActivityGroupActivityId,
  RoomActivityGroupId,
  CascadeActivity,
  WithId,
} from '@/types'

import type { Options } from './BaseService.types'
import BaseService from './BaseService'

/**
 * @private
 */
export default class RoomActivityGroupService extends BaseService<RoomActivityGroup> {
  constructor(client: AxiosInstance) {
    super(client, 'room-activity-group')
  }

  get<R extends DeepPartial<RoomActivityGroup> = RoomActivityGroup>(
    id: RoomActivityGroupId,
    config?: AxiosRequestConfig & Options
  ): Promise<R> {
    let merged = config ?? {}
    if (config?.relations === true) {
      merged = merge({}, merged, {
        params: {
          join: [
            RoomActivityGroupService.ACTIVITY_GROUP_RELATION,
            RoomActivityGroupService.ACTIVITY_GROUP_ACTIVITIES_RELATION,
            RoomActivityGroupService.ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_RELATION,
            RoomActivityGroupService.ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_MEDIA_RELATION,
            RoomActivityGroupService.ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_MEDIA_MEDIA_RELATION,
            RoomActivityGroupService.ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_STEPS_RELATION,
            RoomActivityGroupService.ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_STEPS_MEDIA_RELATION,
            RoomActivityGroupService.ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_STEPS_MEDIA_MEDIA_RELATION,
          ],
        },
      })
    }
    return super.get(id, merged)
  }

  async updateActivityGroup(
    roomActivityGroupId: RoomActivityGroupId,
    activityGroup: ActivityGroup
  ): Promise<RoomActivityGroup> {
    const { data } = await this.client.put<RoomActivityGroup>(
      `/room-activity-group/${roomActivityGroupId}/activity-group`,
      {
        activityGroup,
      }
    )
    return data
  }

  async addActivity(
    roomActivityGroupId: RoomActivityGroupId,
    activityId: ActivityId,
    position: number
  ) {
    const { data } = await this.client.post<RoomActivityGroup>(
      `/room-activity-group/${roomActivityGroupId}/activity-group/activities`,
      {
        activityId,
        position,
      }
    )
    return data
  }

  async removeActivity(
    roomActivityGroupId: RoomActivityGroupId,
    activityGroupActivityId: ActivityGroupActivityId
  ): Promise<RoomActivityGroup> {
    const { data } = await this.client.delete<RoomActivityGroup>(
      `/room-activity-group/${roomActivityGroupId}/activity-group/activities/${activityGroupActivityId}`
    )
    return data
  }

  async updateActivity(
    roomActivityGroupId: RoomActivityGroupId,
    activityGroupActivityId: ActivityGroupActivityId,
    activity: WithId<CascadeActivity>
  ): Promise<RoomActivityGroup> {
    const { data } = await this.client.patch<RoomActivityGroup>(
      `/room-activity-group/${roomActivityGroupId}/activity-group/activities/${activityGroupActivityId}/activity`,
      {
        activity,
      }
    )
    return data
  }

  static ACTIVITY_GROUP_RELATION = 'activityGroup'
  static ACTIVITY_GROUP_ACTIVITIES_RELATION = 'activityGroup.activities'
  static ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_RELATION =
    'activityGroup.activities.activity'
  static ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_MEDIA_RELATION =
    'activityGroup.activities.activity.media'
  static ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_MEDIA_MEDIA_RELATION =
    'activityGroup.activities.activity.media.media'
  static ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_STEPS_RELATION =
    'activityGroup.activities.activity.steps'
  static ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_STEPS_MEDIA_RELATION =
    'activityGroup.activities.activity.steps.media'
  static ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_STEPS_MEDIA_MEDIA_RELATION =
    'activityGroup.activities.activity.steps.media.media'
}
